import { MapPin, Car } from "lucide-react";
import { useAppContext } from "../../context/AppContext";

const LANDMARKS_DB = {
  "new cairo": [
    { name: "AUC New Cairo",          time: "5 mins" },
    { name: "Cairo Festival City",    time: "8 mins" },
    { name: "Point 90 Mall",          time: "10 mins" },
    { name: "Rehab City",             time: "12 mins" },
    { name: "Ring Road",              time: "15 mins" },
  ],
  "sheikh zayed": [
    { name: "Hyper One",              time: "5 mins" },
    { name: "Mall of Arabia",         time: "8 mins" },
    { name: "Smart Village",          time: "10 mins" },
    { name: "6th of October City",    time: "12 mins" },
    { name: "Cairo–Alex Desert Road", time: "7 mins" },
  ],
  "north coast": [
    { name: "Sidi Abdel Rahman",      time: "5 mins" },
    { name: "Marassi Resort",         time: "10 mins" },
    { name: "Hacienda Bay",           time: "15 mins" },
    { name: "Marina Alexandria",      time: "20 mins" },
    { name: "Borg El Arab Airport",   time: "45 mins" },
  ],
  "new capital": [
    { name: "Central Business District", time: "5 mins" },
    { name: "Government District",       time: "8 mins" },
    { name: "Future City",               time: "5 mins" },
    { name: "Suez Road",                 time: "10 mins" },
    { name: "Cairo Int'l Airport",       time: "40 mins" },
  ],
  "heliopolis": [
    { name: "Cairo Int'l Airport",    time: "10 mins" },
    { name: "City Stars Mall",        time: "8 mins" },
    { name: "Korba",                  time: "5 mins" },
    { name: "Nasr City",              time: "12 mins" },
    { name: "Ring Road",              time: "15 mins" },
  ],
  "october": [
    { name: "Mall of Arabia",         time: "5 mins" },
    { name: "Dream Park",             time: "8 mins" },
    { name: "Smart Village",          time: "12 mins" },
    { name: "Juhayna Square",         time: "7 mins" },
    { name: "Cairo–Alex Desert Road", time: "10 mins" },
  ],
};

function getLandmarks(location = "") {
  const loc = location.toLowerCase();
  for (const [key, marks] of Object.entries(LANDMARKS_DB)) {
    if (loc.includes(key)) return marks;
  }
  return [
    { name: "City Center",    time: "10 mins" },
    { name: "Nearest Mall",   time: "12 mins" },
    { name: "Airport",        time: "30 mins" },
    { name: "Highway Access", time: "8 mins" },
    { name: "Metro Station",  time: "15 mins" },
  ];
}

export default function ProjectLocation({ p }) {
  const { lang } = useAppContext();
  const landmarks = p.landmarks && p.landmarks.length > 0 ? p.landmarks : getLandmarks(p.location || "");

  return (
    <section id="pd-location" className="pd-section">
      <div className="fadeUp" style={{ marginBottom: "3rem" }}>
        <span className="pd-section-label">
          {lang === "en" ? "Where You'll Be" : "موقعك المثالي"}
        </span>
        <h2 className="pd-section-title">
          {lang === "en" ? "Location & Landmarks" : "الموقع والمعالم القريبة"}
        </h2>
        <div className="pd-section-bar" />
      </div>

      <div className="pd-loc-grid">
        {/* Map */}
        <div
          className="fadeUp"
          style={{
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            paddingTop: "62%",
            background: "#eae8e4",
            boxShadow: "0 16px 56px rgba(10,22,40,.1)",
            border: "1px solid rgba(201,168,76,.12)",
          }}
        >
          {p.locationLink ? (
            <iframe
              src={p.locationLink}
              title={lang === "en" ? "Project Location" : "موقع المشروع"}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: ".75rem",
              }}
            >
              <MapPin size={44} color="var(--gold)" strokeWidth={1.25} />
              <div
                style={{
                  fontFamily: "var(--serif)",
                  color: "var(--navy)",
                  fontSize: "1.1rem",
                  fontWeight: 300,
                  opacity: 0.7,
                }}
              >
                {lang === "ar" ? p.locationAr || p.location : p.location}
              </div>
              <div
                style={{
                  fontSize: ".72rem",
                  color: "var(--gray)",
                  fontFamily: "var(--sans)",
                  letterSpacing: ".08em",
                }}
              >
                {lang === "en" ? "Map coming soon" : "الخريطة قريباً"}
              </div>
            </div>
          )}
        </div>

        {/* Landmarks list */}
        <div className="fadeUp d2">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: ".65rem",
              marginBottom: "1.75rem",
            }}
          >
            <MapPin size={16} color="var(--gold)" />
            <span
              style={{
                fontFamily: "var(--serif)",
                fontSize: "1.2rem",
                color: "var(--navy)",
                fontWeight: 300,
              }}
            >
              {lang === "ar" ? p.locationAr || p.location : p.location}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
            {landmarks.map((lm, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: ".95rem 1.1rem",
                  background: "#fff",
                  borderRadius: 10,
                  border: "1px solid rgba(201,168,76,.1)",
                  transition: "border-color .2s, box-shadow .2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(201,168,76,.3)";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(201,168,76,.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(201,168,76,.1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: "rgba(201,168,76,.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Car size={15} color="var(--gold)" />
                  </div>
                  <span
                    style={{
                      fontFamily: "var(--sans)",
                      fontSize: ".88rem",
                      color: "var(--navy)",
                      fontWeight: 500,
                    }}
                  >
                    {lm.name}
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: "var(--sans)",
                    fontSize: ".74rem",
                    color: "var(--gold)",
                    fontWeight: 600,
                    background: "rgba(201,168,76,.1)",
                    padding: "3px 10px",
                    borderRadius: 100,
                    flexShrink: 0,
                  }}
                >
                  {lm.time}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
